import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
} from "@mui/material";
import * as React from "react";

import styles from "./AlertDialog.module.css";

export interface AlertDialogProps {
  dialogOpen: boolean;
  handleClose: () => void;
  dialogTitle: string;
  dialogText?: string;
  dialogContent?: React.ReactNode;
  dialogAction?: () => Promise<void>;
  /** Leaves the dialog open once `dialogAction` settles, for a caller that closes it itself. */
  suppressAutoClose?: boolean;
  /** Renders no confirm/cancel row, for a dialog whose content carries its own controls. */
  suppressOptions?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Renders the confirm as the dangerous choice — filled and red against an outlined cancel —
   * so the two options do not read alike. Both are plain text buttons otherwise.
   */
  destructive?: boolean;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({
  dialogOpen,
  handleClose,
  dialogTitle,
  dialogText,
  dialogContent,
  dialogAction = () => Promise.resolve(),
  suppressAutoClose,
  suppressOptions,
  confirmLabel = "Yes",
  cancelLabel = "No",
  destructive,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(false);

  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      // This absolutely needs to be here so that the dialog can be used in the viz workspace
      // because of https://github.com/roboto-ai/roboto-hosted-app/blob/main/web-ui/src/shared/components/visualization/DndContainer/PointerSensor.ts#L7
      data-not-draggable="true"
      maxWidth="md"
      sx={{ zIndex: theme.zLayer(10) }}
    >
      <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
      <DialogContent>
        {dialogText && (
          <DialogContentText id="alert-dialog-description">
            {dialogText}
          </DialogContentText>
        )}
        {dialogContent}
      </DialogContent>
      {loading ? (
        <DialogActions className={`${styles.actions} ${styles.loadingRow}`}>
          <CircularProgress size={"1.5rem"} />
        </DialogActions>
      ) : (
        // The buttons are direct children of DialogActions so its own sibling spacing applies;
        // wrapping them in one element would suppress it.
        !suppressOptions && (
          <DialogActions className={styles.actions}>
            <Button
              variant={destructive ? "outlined" : undefined}
              onClick={handleClose}
            >
              {cancelLabel}
            </Button>

            <Button
              variant={destructive ? "contained" : undefined}
              color={destructive ? "error" : undefined}
              data-cy="confirm-action"
              onClick={() => {
                setLoading(true);
                void dialogAction().finally(() => {
                  if (!suppressAutoClose) {
                    setLoading(false);
                    handleClose();
                  }
                });
              }}
              autoFocus
            >
              {confirmLabel}
            </Button>
          </DialogActions>
        )
      )}
    </Dialog>
  );
};
